'use client';

import React, { useState, useEffect } from 'react';
import { CloudSnow, Search, Info, Sliders, MapPin, Wind, Thermometer, AlertTriangle, HelpCircle, Star, School } from 'lucide-react';
import AdUnit from '@/components/AdUnit';

interface ForecastDay {
  date: string;
  weekday: string;
  snowfall: number; // inches
  tempMin: number; // °F
  windMax: number; // mph
  weatherCode: number;
  precipitationProb: number;
  probability: number;
  description: string;
  verdict: string;
}

export default function SnowDayCalculator() {
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState<'us' | 'ca'>('us');
  const [schoolType, setSchoolType] = useState<'public' | 'private'>('public');
  const [snowDaysUsed, setSnowDaysUsed] = useState(0);
  
  // App Mode: 'forecast' or 'simulation'
  const [mode, setMode] = useState<'forecast' | 'simulation'>('forecast');
  
  // Simulation Inputs
  const [simSnowfall, setSimSnowfall] = useState(4); // inches
  const [simTemp, setSimTemp] = useState(24); // °F
  const [simWind, setSimWind] = useState(15); // mph
  const [simIce, setSimIce] = useState(false); // ice/freezing rain
  
  // Forecast Data States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [forecastDays, setForecastDays] = useState<ForecastDay[]>([]);
  
  // Run simulation calculation when manual values change
  useEffect(() => {
    if (mode === 'simulation') {
      calculateSimulation();
    }
  }, [mode, simSnowfall, simTemp, simWind, simIce, schoolType, snowDaysUsed]);

  // Main calculation algorithm for Snow Day Probability
  const getProbabilityScore = (
    snowfall: number, // inches
    tempMin: number, // °F
    windMax: number, // mph
    isIce: boolean,
    school: 'public' | 'private',
    daysUsed: number
  ): { score: number; verdict: string; details: string[] } => {
    if (snowfall <= 0.05 && !isIce && tempMin > 32) {
      return {
        score: 0,
        verdict: 'School is open. No winter weather in forecast.',
        details: ['No significant snowfall or sub-freezing temperatures expected.']
      };
    }

    let score = 0;
    const details: string[] = [];

    // 1. Snowfall Contribution (inches)
    if (snowfall > 0) {
      if (snowfall < 1) {
        score += snowfall * 15; // up to 15%
        details.push(`Light dusting expected (${snowfall.toFixed(1)}"): +${Math.round(snowfall * 15)}%`);
      } else if (snowfall <= 3) {
        score += 15 + (snowfall - 1) * 20; // up to 55%
        details.push(`Minor accumulation expected (${snowfall.toFixed(1)}"): +${Math.round(15 + (snowfall - 1) * 20)}%`);
      } else if (snowfall <= 6) {
        score += 55 + (snowfall - 3) * 10; // up to 85%
        details.push(`Moderate accumulation forecast (${snowfall.toFixed(1)}"): +${Math.round(55 + (snowfall - 3) * 10)}%`);
      } else {
        score += 85 + Math.min(10, (snowfall - 6) * 2.5); // up to 95%
        details.push(`Heavy snow alert (${snowfall.toFixed(1)}"): High probability modifier (+${Math.round(85 + Math.min(10, (snowfall - 6) * 2.5))})`);
      }
    }

    // 2. Temperature Contribution
    if (tempMin <= 0) {
      score += 25;
      details.push('Dangerous sub-zero temperatures (frostbite warning): +25%');
    } else if (tempMin <= 15) {
      score += 18;
      details.push('Deep freeze conditions (<15°F): +18%');
    } else if (tempMin <= 25) {
      score += 10;
      details.push('Slippery conditions & hard freeze (<25°F): +10%');
    } else if (tempMin <= 32) {
      score += 5;
      details.push('Temperatures below freezing (<32°F): +5%');
    }

    // 3. Wind Speed Contribution
    if (windMax >= 35) {
      score += 20;
      details.push('High winds causing blowing snow/poor visibility (>35 mph): +20%');
    } else if (windMax >= 22) {
      score += 12;
      details.push('Gusty winds with risk of drifting snow (>22 mph): +12%');
    } else if (windMax >= 12) {
      score += 5;
      details.push('Moderate breeze (>12 mph): +5%');
    }

    // 4. Ice/Freezing Rain Modifier (Very dangerous for school buses)
    if (isIce) {
      score += 25;
      details.push('Freezing rain or ice storm predicted (extreme road hazard): +25%');
    }

    // 5. School Type Modifier
    if (school === 'private') {
      score += 5;
      details.push('Private School modifier (more flexible closure policies): +5%');
    }

    // 6. Snow Days Used Modifier (Reluctance to extend the school year)
    if (daysUsed === 0) {
      score += 5;
      details.push('No snow days used yet (superintendent buffer intact): +5%');
    } else if (daysUsed === 3) {
      score -= 5;
      details.push('3 snow days used (moderate reluctance to close): -5%');
    } else if (daysUsed === 4) {
      score -= 12;
      details.push('4 snow days used (high reluctance, makeup days looming): -12%');
    } else if (daysUsed >= 5) {
      score -= 22;
      details.push('5+ snow days used (extreme reluctance to cancel): -22%');
    }

    // Clip score
    const finalScore = Math.max(0, Math.min(99, Math.round(score)));

    // Choose verdict
    let verdict = '';
    if (finalScore < 10) {
      verdict = 'School is almost certainly open. Sleep well and do your homework!';
    } else if (finalScore < 30) {
      verdict = 'Low probability. Pack your bags, but keep an eye on minor developments.';
    } else if (finalScore < 60) {
      verdict = 'Solid chance! Sleep with your pajamas inside out. A delay or cancellation is possible.';
    } else if (finalScore < 85) {
      verdict = 'Very high chance! Prepare for a sleep-in or snow day announcement.';
    } else {
      verdict = 'Virtually guaranteed snow day! Clear your schedule and get your sled ready!';
    }

    return { score: finalScore, verdict, details };
  };

  // Run calculation for simulation mode
  const calculateSimulation = () => {
    const calc = getProbabilityScore(
      simSnowfall,
      simTemp,
      simWind,
      simIce,
      schoolType,
      snowDaysUsed
    );

    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    setForecastDays([
      {
        date: tomorrow.toLocaleDateString([], { month: 'short', day: 'numeric' }),
        weekday: weekdays[tomorrow.getDay()],
        snowfall: simSnowfall,
        tempMin: simTemp,
        windMax: simWind,
        weatherCode: simIce ? 66 : 71,
        precipitationProb: 90,
        probability: calc.score,
        description: simIce ? 'Freezing Rain' : 'Heavy Snow',
        verdict: calc.verdict
      }
    ]);
  };

  // Fetch forecast using ZIP/Postal code
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!zipCode.trim()) return;

    setLoading(true);
    setError(null);
    setLocationName(null);
    setForecastDays([]);

    try {
      // 1. Sanitize ZIP code and query zippopotam.us
      const queryZip = zipCode.trim().replace(/\s+/g, '').toUpperCase();
      
      // For Canada, we need the forward sortation area (first 3 characters)
      let geocodeUrl = `https://api.zippopotam.us/${country}/${queryZip}`;
      if (country === 'ca') {
        const fsa = queryZip.substring(0, 3);
        geocodeUrl = `https://api.zippopotam.us/ca/${fsa}`;
      }

      const geoRes = await fetch(geocodeUrl);
      if (!geoRes.ok) {
        throw new Error('Invalid zip code or location details not found. Please try another.');
      }

      const geoData = await geoRes.json();
      const place = geoData.places[0];
      const lat = parseFloat(place.latitude);
      const lon = parseFloat(place.longitude);
      const placeName = `${place['place name']}, ${place['state abbreviation'] || geoData.state || geoData.country}`;
      setLocationName(placeName);

      // 2. Query Open-Meteo with coordinates
      const weatherUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=temperature_2m_max,temperature_2m_min,snowfall_sum,wind_speed_10m_max,precipitation_probability_max,weather_code&precipitation_unit=inch&wind_speed_unit=mph&timezone=auto`;

      const weatherRes = await fetch(weatherUrl);
      if (!weatherRes.ok) {
        throw new Error('Failed to retrieve forecast data from the meteorological server.');
      }

      const weatherData = await weatherRes.json();
      const daily = weatherData.daily;

      if (!daily || !daily.time) {
        throw new Error('No weather forecast available for this coordinates.');
      }

      const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

      const parsedForecast: ForecastDay[] = daily.time.slice(0, 4).map((timeStr: string, idx: number) => {
        const dateObj = new Date(timeStr + 'T00:00:00');
        
        // Open-Meteo snowfall_sum is returned in inches due to precipitation_unit=inch
        const snowfall = parseFloat((daily.snowfall_sum[idx] || 0).toFixed(1));
        const tempMin = Math.round(daily.temperature_2m_min[idx] || 32);
        const windMax = Math.round(daily.wind_speed_10m_max[idx] || 0);
        const code = daily.weather_code[idx] || 0;
        const precipProb = daily.precipitation_probability_max[idx] || 0;

        // Determine if weather code indicates freezing rain/ice
        // WMO codes: 56, 57 (freezing drizzle), 66, 67 (freezing rain)
        const isIce = [56, 57, 66, 67].includes(code);

        const calc = getProbabilityScore(
          snowfall,
          tempMin,
          windMax,
          isIce,
          schoolType,
          snowDaysUsed
        );

        // Get weather condition text
        let conditionDesc = 'Clear';
        if (code >= 71 && code <= 77) conditionDesc = 'Snowing';
        else if (code >= 85 && code <= 86) conditionDesc = 'Snow Showers';
        else if (isIce) conditionDesc = 'Freezing Rain/Ice';
        else if (code >= 61 && code <= 65) conditionDesc = 'Raining';
        else if (code >= 1 && code <= 3) conditionDesc = 'Partly Cloudy';
        else if (code >= 45 && code <= 48) conditionDesc = 'Foggy';

        return {
          date: dateObj.toLocaleDateString([], { month: 'short', day: 'numeric' }),
          weekday: weekdays[dateObj.getDay()],
          snowfall,
          tempMin,
          windMax,
          weatherCode: code,
          precipitationProb: precipProb,
          probability: calc.score,
          description: conditionDesc,
          verdict: calc.verdict
        };
      });

      setForecastDays(parsedForecast);
    } catch (err: any) {
      setError(err.message || 'An error occurred during calculation.');
    } finally {
      setLoading(false);
    }
  };

  const getDialColor = (prob: number) => {
    if (prob < 15) return 'stroke-emerald-500';
    if (prob < 40) return 'stroke-yellow-400';
    if (prob < 75) return 'stroke-sky-400';
    return 'stroke-blue-500';
  };

  const getDialBgColor = (prob: number) => {
    if (prob < 15) return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
    if (prob < 40) return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
    if (prob < 75) return 'bg-sky-500/10 text-sky-400 border-sky-500/20';
    return 'bg-blue-500/15 text-blue-400 border-blue-500/30';
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 font-sans pb-16">
      {/* Background gradients */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.15),transparent_50%)] pointer-events-none z-0" />

      <div className="max-w-4xl mx-auto px-4 py-8 relative z-10">

        {/* AdSense Unit - Top */}
        <AdUnit />

        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-semibold mb-3">
            <CloudSnow className="w-3.5 h-3.5" />
            Predictive School Closure Engine
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-sky-200 via-blue-100 to-indigo-200 tracking-tight mb-3">
            School Snow Day Predictor
          </h1>
          <p className="text-slate-400 text-sm md:text-base max-w-lg mx-auto leading-relaxed">
            Estimate the likelihood of school closure or delays using hyper-local forecasting model. Enter your ZIP code to get started.
          </p>
        </div>

        {/* Settings and Config Panel */}
        <div className="bg-slate-800/60 backdrop-blur-xl border border-slate-750 rounded-3xl p-6 md:p-8 shadow-2xl mb-8">
          
          {/* Mode Selector */}
          <div className="grid grid-cols-2 p-1.5 bg-slate-900 border border-slate-850 rounded-2xl mb-8 max-w-md mx-auto">
            <button
              onClick={() => { setMode('forecast'); setError(null); }}
              className={`py-2 text-xs md:text-sm font-semibold rounded-xl flex items-center justify-center gap-2 transition-all ${
                mode === 'forecast' ? 'bg-sky-600 text-white shadow-lg shadow-sky-900/35' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <MapPin className="w-4 h-4" />
              Live Zip Forecast
            </button>
            <button
              onClick={() => { setMode('simulation'); setError(null); }}
              className={`py-2 text-xs md:text-sm font-semibold rounded-xl flex items-center justify-center gap-2 transition-all ${
                mode === 'simulation' ? 'bg-sky-600 text-white shadow-lg shadow-sky-900/35' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <Sliders className="w-4 h-4" />
              Scenario Simulator
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            
            {/* Input Details */}
            <div className="md:col-span-1 space-y-5 bg-slate-900/40 border border-slate-800/80 p-5 rounded-2xl">
              
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <Sliders className="w-3.5 h-3.5 text-sky-400" />
                Parameters
              </h3>

              {mode === 'forecast' ? (
                <form onSubmit={handleSearch} className="space-y-3">
                  <label className="block text-xs font-semibold text-slate-300">
                    Location Search:
                  </label>
                  <div className="flex gap-1.5">
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value as 'us' | 'ca')}
                      className="bg-slate-900 border border-slate-800 rounded-xl px-2 py-2 text-xs font-bold text-slate-200 focus:outline-none focus:border-sky-500"
                    >
                      <option value="us">🇺🇸 US</option>
                      <option value="ca">🇨🇦 CA</option>
                    </select>
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value)}
                        placeholder={country === 'us' ? 'Zip code (e.g. 10001)' : 'Postal (e.g. K1A)'}
                        className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-3 pr-8 py-2 text-xs font-bold text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500"
                      />
                      <button
                        type="submit"
                        className="absolute right-2 top-2 text-slate-400 hover:text-sky-400 transition"
                      >
                        <Search className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  {/* Snowfall slider */}
                  <div>
                    <div className="flex justify-between text-xs font-semibold mb-1">
                      <span className="text-slate-300">Snow Accumulation:</span>
                      <span className="text-sky-400 font-bold">{simSnowfall.toFixed(1)} inches</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="15"
                      step="0.5"
                      value={simSnowfall}
                      onChange={(e) => setSimSnowfall(parseFloat(e.target.value))}
                      className="w-full accent-sky-500 bg-slate-850 h-1.5 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Temp Slider */}
                  <div>
                    <div className="flex justify-between text-xs font-semibold mb-1">
                      <span className="text-slate-300">Min Temperature:</span>
                      <span className="text-sky-400 font-bold">{simTemp}°F</span>
                    </div>
                    <input
                      type="range"
                      min="-15"
                      max="40"
                      value={simTemp}
                      onChange={(e) => setSimTemp(parseInt(e.target.value))}
                      className="w-full accent-sky-500 bg-slate-850 h-1.5 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Wind Slider */}
                  <div>
                    <div className="flex justify-between text-xs font-semibold mb-1">
                      <span className="text-slate-300">Wind Gusts:</span>
                      <span className="text-sky-400 font-bold">{simWind} mph</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="50"
                      value={simWind}
                      onChange={(e) => setSimWind(parseInt(e.target.value))}
                      className="w-full accent-sky-500 bg-slate-850 h-1.5 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Ice Checkbox */}
                  <label className="flex items-center gap-2 cursor-pointer py-1 select-none">
                    <input
                      type="checkbox"
                      checked={simIce}
                      onChange={(e) => setSimIce(e.target.checked)}
                      className="w-4 h-4 rounded text-sky-600 bg-slate-900 border-slate-800 focus:ring-sky-500 accent-sky-500"
                    />
                    <span className="text-xs font-semibold text-slate-300">Freezing Rain / Ice storm</span>
                  </label>
                </div>
              )}

              {/* Common Modifiers */}
              <div className="border-t border-slate-800/80 pt-4 space-y-3">
                
                {/* School Type */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                    School District Type:
                  </label>
                  <div className="grid grid-cols-2 gap-2 p-1 bg-slate-900 border border-slate-800 rounded-xl">
                    <button
                      onClick={() => setSchoolType('public')}
                      className={`py-1 text-xs font-semibold rounded-lg transition-all ${
                        schoolType === 'public' ? 'bg-sky-600 text-white' : 'text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      Public
                    </button>
                    <button
                      onClick={() => setSchoolType('private')}
                      className={`py-1 text-xs font-semibold rounded-lg transition-all ${
                        schoolType === 'private' ? 'bg-sky-600 text-white' : 'text-slate-400 hover:text-slate-200'
                      }`}
                    >
                      Private
                    </button>
                  </div>
                </div>

                {/* Snow Days Already Taken */}
                <div>
                  <div className="flex justify-between text-xs font-semibold mb-1">
                    <span className="text-slate-300">Snow Days Used:</span>
                    <span className="text-sky-400 font-bold">{snowDaysUsed} days</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="6"
                    value={snowDaysUsed}
                    onChange={(e) => setSnowDaysUsed(parseInt(e.target.value))}
                    className="w-full accent-sky-500 bg-slate-850 h-1.5 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-[9px] text-slate-400 mt-1 block">
                    Districts with high snow day usage become extremely reluctant to close.
                  </span>
                </div>

              </div>

            </div>

            {/* Outputs / Gauge Display */}
            <div className="md:col-span-2 space-y-6">
              
              {loading && (
                <div className="h-64 flex flex-col items-center justify-center text-slate-400 gap-3">
                  <div className="w-10 h-10 border-4 border-sky-500/20 border-t-sky-500 rounded-full animate-spin" />
                  <p className="text-sm font-semibold">Consulting local weather forecast...</p>
                </div>
              )}

              {error && (
                <div className="p-5 bg-red-500/10 border border-red-500/25 rounded-2xl flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs font-bold text-red-400 block mb-0.5">Forecast Query Failure</span>
                    <p className="text-xs text-red-200 leading-relaxed">{error}</p>
                  </div>
                </div>
              )}

              {!loading && !error && forecastDays.length === 0 && (
                <div className="h-64 bg-slate-900/30 border border-dashed border-slate-800 rounded-2xl flex flex-col items-center justify-center text-center p-6 text-slate-400">
                  <CloudSnow className="w-12 h-12 text-slate-700 mb-3 animate-bounce" />
                  <p className="text-sm font-bold text-slate-300">Enter Zip Code or Toggle Scenario Simulator</p>
                  <p className="text-xs max-w-xs mt-1">Get precise percentage predictions for school closures based on live meteorological data.</p>
                </div>
              )}

              {!loading && !error && forecastDays.length > 0 && (
                <div className="space-y-6 animate-in fade-in duration-300">
                  
                  {/* Location Info Banner */}
                  {locationName && (
                    <div className="bg-sky-500/5 border border-sky-500/15 rounded-xl px-4 py-2 text-xs font-semibold text-sky-300 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-sky-400" />
                      Showing live forecast for: <span className="text-slate-100 font-bold">{locationName}</span>
                    </div>
                  )}

                  {/* Primary Day Closure Gauge */}
                  <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-6 shadow-inner">
                    
                    {/* Ring gauge */}
                    <div className="relative w-36 h-36 flex-shrink-0">
                      <svg className="w-full h-full transform -rotate-95" viewBox="0 0 100 100">
                        <circle
                          className="text-slate-800 stroke-current"
                          strokeWidth="8"
                          fill="transparent"
                          r="38"
                          cx="50"
                          cy="50"
                        />
                        <circle
                          className={`${getDialColor(forecastDays[0].probability)} stroke-current`}
                          strokeWidth="8"
                          strokeDasharray={2 * Math.PI * 38}
                          strokeDashoffset={2 * Math.PI * 38 * (1 - forecastDays[0].probability / 100)}
                          strokeLinecap="round"
                          fill="transparent"
                          r="38"
                          cx="50"
                          cy="50"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-3xl font-black tracking-tight text-white">{forecastDays[0].probability}%</span>
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Chance</span>
                      </div>
                    </div>

                    {/* Verdict Box */}
                    <div className="flex-1 space-y-2">
                      <div className="text-[10px] font-bold text-sky-400 uppercase tracking-widest">
                        {mode === 'simulation' ? 'Simulation Result' : `Prediction for ${forecastDays[0].weekday}`}
                      </div>
                      <h4 className="text-lg font-extrabold text-white leading-tight">
                        {forecastDays[0].verdict}
                      </h4>
                      <p className="text-xs text-slate-400 leading-relaxed">
                        Expected snowfall of **{forecastDays[0].snowfall}"** with lows around **{forecastDays[0].tempMin}°F** and wind gusts peaking at **{forecastDays[0].windMax} mph**.
                      </p>
                    </div>

                  </div>

                  {/* Multi-day forecast list (Live mode only) */}
                  {mode === 'forecast' && forecastDays.length > 1 && (
                    <div className="space-y-3">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest px-1">
                        Upcoming Days Closure Chances
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {forecastDays.slice(1).map((day, idx) => (
                          <div key={idx} className="bg-slate-900/30 border border-slate-800 rounded-xl p-4 flex flex-col justify-between hover:border-slate-700 transition">
                            <div>
                              <div className="flex justify-between items-start mb-2">
                                <span className="text-xs font-bold text-slate-200">{day.weekday}</span>
                                <span className="text-[10px] text-slate-400">{day.date}</span>
                              </div>
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-extrabold border ${getDialBgColor(day.probability)} mb-3`}>
                                {day.probability}% Chance
                              </span>
                            </div>
                            <div className="space-y-1.5 pt-2 border-t border-slate-800/40 text-[11px] text-slate-400">
                              <div className="flex justify-between">
                                <span>Snowfall:</span>
                                <span className="font-bold text-slate-200">{day.snowfall}"</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Condition:</span>
                                <span className="font-bold text-slate-200">{day.description}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Min Temp:</span>
                                <span className="font-bold text-slate-200">{day.tempMin}°F</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              )}

            </div>

          </div>
        </div>

        {/* AdSense Unit - Bottom */}
        <AdUnit />

        {/* Educational Section / SDC Guide */}
        <div className="space-y-8 bg-slate-900/25 border border-slate-900 rounded-3xl p-6 md:p-10">
          
          <h2 className="text-2xl font-bold tracking-tight text-white flex items-center gap-2">
            <span className="w-1.5 h-6 bg-sky-500 rounded-full" />
            How the Snow Day Calculator Predicts Closures
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-slate-300 text-sm leading-relaxed">
            
            <div className="space-y-4">
              <h3 className="text-base font-bold text-white flex items-center gap-1.5">
                <Info className="w-4.5 h-4.5 text-sky-400" />
                The Core Closure Factors
              </h3>
              <p>
                School superintendents have to make a tough call by 5:00 AM on storm mornings. The main considerations are:
              </p>
              <p>
                *   **Bus Safety (Primary):** School buses are heavy and have long braking distances. Accumulations over 3 inches, or ice on secondary side-streets, makes bus routes dangerous.
                *   **Storm Timing:** Heavy snow starting at 4:00 AM disrupts road plow operations during the critical commute window. Mid-day storms may trigger early dismissals rather than cancellations.
                *   **Wind Chill Danger:** Even with 0 snowfall, temperatures below 0°F can trigger closures to prevent children from frostbite while waiting at outdoor bus stops.
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-base font-bold text-white flex items-center gap-1.5">
                <AlertTriangle className="w-4.5 h-4.5 text-amber-400" />
                Ice Storms vs. Snow Storms
              </h3>
              <p>
                **Ice is vastly more dangerous than snow.** While a plow can clear 4 inches of fluffy snow, freezing rain creates black ice sheets that cannot be plowed and require extensive salting.
              </p>
              <p>
                Weather forecast models use WMO weather codes: **66 and 67** mean freezing rain, and **56 and 57** mean freezing drizzle. If these conditions are forecasted, the calculator automatically boosts probability levels because ice causes traffic standstills and downs tree branches/powerlines.
              </p>
            </div>
            
          </div>

          <hr className="border-slate-800" />

          {/* SDC FAQ */}
          <div className="space-y-4">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <HelpCircle className="w-4.5 h-4.5 text-sky-400" />
              Frequently Asked Questions
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-xl space-y-1">
                <span className="text-sky-300 font-bold block">Why is a 95%+ score not 100%?</span>
                No algorithm can replace the final decision of a school superintendent. Factors like district liability, parent pushback, and immediate plow effectiveness introduce a human margin of error.
              </div>
              <div className="bg-slate-900/40 border border-slate-800 p-4 rounded-xl space-y-1">
                <span className="text-sky-300 font-bold block">How do "Used Snow Days" affect predictions?</span>
                School years have a built-in buffer of "forgiven" snow days. When a district uses all their days, further closures require extending the school year into summer. Superintendents become highly hesitant to cancel classes at that stage.
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
